import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogClose
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  Plus,
  Search,
  Target,
  Zap,
  Settings,
  DollarSign,
  Handshake,
  Boxes,
  ChevronRight,
  Edit,
  Trash2,
  MessageSquare,
  Calendar,
  User,
  Clock,
  CheckCircle,
  XCircle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  p0: { label: 'P0 - Critical', color: 'bg-red-500', textColor: 'text-red-400' },
  p1: { label: 'P1 - High', color: 'bg-orange-500', textColor: 'text-orange-400' },
  p2: { label: 'P2 - Medium', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  p3: { label: 'P3 - Low', color: 'bg-blue-500', textColor: 'text-blue-400' },
};

const STATUS_CONFIG = {
  proposed: { label: 'Proposed', color: 'bg-slate-500', icon: Clock },
  approved: { label: 'Approved', color: 'bg-purple-500', icon: CheckCircle },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  deferred: { label: 'Deferred', color: 'bg-gray-500', icon: Clock },
  cancelled: { label: 'Cancelled', color: 'bg-red-800', icon: XCircle },
};

const CATEGORY_CONFIG = {
  feature: { label: 'Feature', icon: Zap, color: 'text-purple-400' },
  enhancement: { label: 'Enhancement', icon: Settings, color: 'text-blue-400' },
  integration: { label: 'Integration', icon: Boxes, color: 'text-green-400' },
  infrastructure: { label: 'Infrastructure', icon: Settings, color: 'text-orange-400' },
  monetization: { label: 'Monetization', icon: DollarSign, color: 'text-emerald-400' },
  partnership: { label: 'Partnership', icon: Handshake, color: 'text-cyan-400' },
};

const OperationsRoadmap = ({ token }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'feature',
    priority: 'p2',
    estimated_effort: '',
    target_quarter: '',
    tags: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterPriority, filterStatus, filterCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPriority !== 'all') params.append('priority', filterPriority);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      
      const [itemsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/vbb/roadmap?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/vbb/roadmap/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (itemsRes.ok) {
        setItems(await itemsRes.json());
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch roadmap:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/vbb/roadmap`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
        })
      });

      if (res.ok) {
        toast.success('Roadmap item created');
        setCreateDialogOpen(false);
        setFormData({
          title: '', description: '', category: 'feature', priority: 'p2',
          estimated_effort: '', target_quarter: '', tags: ''
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create item');
      }
    } catch (err) {
      toast.error('Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/vbb/roadmap/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success('Status updated');
        fetchData();
        if (selectedItem?.id === itemId) {
          setSelectedItem(await res.json());
        }
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedItem) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/vbb/roadmap/${selectedItem.id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note: newNote })
      });

      if (res.ok) {
        const updated = await res.json();
        setSelectedItem(updated);
        setNewNote('');
        toast.success('Note added');
      }
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm('Delete this roadmap item?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/vbb/roadmap/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Item deleted');
        setDetailDialogOpen(false);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const filteredItems = items.filter(item => 
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openDetail = (item) => {
    setSelectedItem(item);
    setDetailDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-sm text-slate-400">Total Items</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-400">{stats.by_status?.in_progress || 0}</div>
              <div className="text-sm text-slate-400">In Progress</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-400">{stats.by_status?.completed || 0}</div>
              <div className="text-sm text-slate-400">Completed</div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-400">{stats.by_priority?.p0 || 0}</div>
              <div className="text-sm text-slate-400">Critical (P0)</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters & Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search roadmap..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white w-64"
            />
          </div>
          
          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Priorities</SelectItem>
              {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Button 
          onClick={() => setCreateDialogOpen(true)}
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
      </div>

      {/* Roadmap Items List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white">No roadmap items</h3>
            <p className="text-slate-400 mt-1">Create your first roadmap item to get started</p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="mt-4 bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredItems.map(item => {
            const CategoryIcon = CATEGORY_CONFIG[item.category]?.icon || Target;
            const StatusIcon = STATUS_CONFIG[item.status]?.icon || Clock;
            
            return (
              <Card 
                key={item.id} 
                className="bg-slate-800/50 border-slate-700 hover:border-slate-600 cursor-pointer transition-colors"
                onClick={() => openDetail(item)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg bg-slate-700/50 ${CATEGORY_CONFIG[item.category]?.color}`}>
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-white truncate">{item.title}</h3>
                          <Badge className={`${PRIORITY_CONFIG[item.priority]?.color} text-white text-xs`}>
                            {item.priority.toUpperCase()}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-400 line-clamp-2">{item.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          {item.target_quarter && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {item.target_quarter}
                            </span>
                          )}
                          {item.estimated_effort && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {item.estimated_effort}
                            </span>
                          )}
                          {item.notes?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {item.notes.length} notes
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${STATUS_CONFIG[item.status]?.color} text-white`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {STATUS_CONFIG[item.status]?.label}
                      </Badge>
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Roadmap Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="bg-slate-700 border-slate-600"
                placeholder="Feature or enhancement title"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-slate-700 border-slate-600 min-h-[100px]"
                placeholder="Detailed description, requirements, and context..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estimated Effort</Label>
                <Input
                  value={formData.estimated_effort}
                  onChange={(e) => setFormData({...formData, estimated_effort: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                  placeholder="e.g., 2-3 days"
                />
              </div>
              <div>
                <Label>Target Quarter</Label>
                <Input
                  value={formData.target_quarter}
                  onChange={(e) => setFormData({...formData, target_quarter: e.target.value})}
                  className="bg-slate-700 border-slate-600"
                  placeholder="e.g., Q2 2026"
                />
              </div>
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                className="bg-slate-700 border-slate-600"
                placeholder="e.g., i18n, ux, mobile"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl">{selectedItem.title}</DialogTitle>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={`${PRIORITY_CONFIG[selectedItem.priority]?.color} text-white`}>
                        {selectedItem.priority.toUpperCase()}
                      </Badge>
                      <Badge className={`${STATUS_CONFIG[selectedItem.status]?.color} text-white`}>
                        {STATUS_CONFIG[selectedItem.status]?.label}
                      </Badge>
                      <Badge variant="outline" className="border-slate-600 text-slate-300">
                        {CATEGORY_CONFIG[selectedItem.category]?.label}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Description */}
                <div>
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Description</h4>
                  <div className="text-white whitespace-pre-wrap bg-slate-700/50 p-3 rounded-lg">
                    {selectedItem.description || 'No description provided'}
                  </div>
                </div>
                
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedItem.estimated_effort && (
                    <div>
                      <span className="text-slate-400">Effort:</span>
                      <span className="text-white ml-2">{selectedItem.estimated_effort}</span>
                    </div>
                  )}
                  {selectedItem.target_quarter && (
                    <div>
                      <span className="text-slate-400">Target:</span>
                      <span className="text-white ml-2">{selectedItem.target_quarter}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-slate-400">Created by:</span>
                    <span className="text-white ml-2">{selectedItem.created_by_name}</span>
                  </div>
                  <div>
                    <span className="text-slate-400">Created:</span>
                    <span className="text-white ml-2">{new Date(selectedItem.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {/* Status Change */}
                <div>
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Change Status</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <Button
                        key={key}
                        size="sm"
                        variant={selectedItem.status === key ? "default" : "outline"}
                        className={selectedItem.status === key ? cfg.color : "border-slate-600"}
                        onClick={() => handleStatusChange(selectedItem.id, key)}
                      >
                        {cfg.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <h4 className="text-sm font-medium text-slate-400 mb-2">
                    Notes ({selectedItem.notes?.length || 0})
                  </h4>
                  <div className="space-y-3 mb-3">
                    {selectedItem.notes?.map(note => (
                      <div key={note.id} className="bg-slate-700/50 p-3 rounded-lg">
                        <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                          <User className="w-3 h-3" />
                          {note.author_name}
                          <span>•</span>
                          {new Date(note.created_at).toLocaleString()}
                        </div>
                        <p className="text-white text-sm">{note.note}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note..."
                      className="bg-slate-700 border-slate-600"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    />
                    <Button onClick={handleAddNote} disabled={saving || !newNote.trim()}>
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </Button>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="border-t border-slate-700 pt-4">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => handleDelete(selectedItem.id)}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
                <DialogClose asChild>
                  <Button variant="outline">Close</Button>
                </DialogClose>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OperationsRoadmap;
